import React, { useState } from 'react';
import { BulkActionsBar as SparkBulkActionsBar, ModalFeedback, SparkModalPortal } from '@spark';
import {
    bulkDeletePrimaryLabel,
    bulkDeleteTitle,
    renderBoldNameList,
} from '@/features/tasks/utils/bulkDeleteCopy';
import { MoveTasksModal } from './MoveTasksModal.jsx';
import { CardContextMenu } from './CardContextMenu.jsx';

const taskDeleteBody = (names) => {
    const nameList = renderBoldNameList(names);
    return (
        <>
            All data, prompts, evaluations, and models associated with{' '}
            {nameList || (names.length === 1 ? 'this task' : 'these tasks')} will be permanently
            deleted. This action can’t be undone.
        </>
    );
};

const projectDeleteBody = (names, count) => (
    <>
        {count === 1 ? 'The project ' : 'The projects '}
        {renderBoldNameList(names)}
        {count === 1
            ? ' and everything associated with it, including tasks, data, and evaluations, will be permanently deleted. This action can’t be undone.'
            : ' and everything associated with them, including tasks, data, and evaluations, will be permanently deleted. This action can’t be undone.'}
    </>
);

export const UsecasesBulkActionsBar = ({
    selectedCount,
    onSelectAll,
    onDeselectAll,
    totalCount,
    onDelete,
    onMove,
    destinations = [],
    disabled = false,
    entityNoun = 'task',
    itemNames = [],
    variant = 'default',
    contextMenu = null,
    onContextMenuClose,
}) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const [showMove, setShowMove] = useState(false);
    const [moveLoading, setMoveLoading] = useState(false);

    const handleDeleteClick = () => {
        setShowConfirm(true);
    };

    const handleConfirmDelete = () => {
        setShowConfirm(false);
        onDelete?.();
    };

    const handleMove = async (destName) => {
        if (!onMove) return;
        setMoveLoading(true);
        try {
            const result = await onMove(destName);
            if (result?.success !== false) {
                setShowMove(false);
            }
        } finally {
            setMoveLoading(false);
        }
    };

    const actions = [];
    if (onMove) {
        actions.push({
            label: 'Move Task',
            iconSlug: 'export',
            disableWhenReadOnly: true,
            onClick: () => setShowMove(true),
        });
    }
    actions.push({
        label: 'Delete',
        iconSlug: 'trash',
        disableWhenReadOnly: true,
        onClick: handleDeleteClick,
    });

    return (
        <>
            <SparkBulkActionsBar
                selectedCount={selectedCount}
                totalCount={totalCount}
                onSelectAll={onSelectAll}
                onDeselectAll={onDeselectAll}
                showDeselectAll={false}
                actions={actions}
                disabled={disabled}
                variant={variant}
            />

            {contextMenu ? (
                <CardContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onClose={onContextMenuClose}
                    items={actions.map(({ label, onClick, disableWhenReadOnly }) => ({ label, onClick, disableWhenReadOnly }))}
                />
            ) : null}

            <SparkModalPortal
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                size="text"
            >
                <ModalFeedback
                    feedbackType="warning"
                    title={bulkDeleteTitle(selectedCount, entityNoun, itemNames)}
                    content={
                        entityNoun === 'project'
                            ? projectDeleteBody(itemNames, selectedCount)
                            : taskDeleteBody(itemNames)
                    }
                    showOverlay={false}
                    danger
                    primaryLabel={bulkDeletePrimaryLabel(selectedCount, entityNoun)}
                    tertiaryLabel="Cancel"
                    primaryDisableWhenReadOnly
                    onPrimaryClick={handleConfirmDelete}
                    onTertiaryClick={() => setShowConfirm(false)}
                    onCloseClick={() => setShowConfirm(false)}
                />
            </SparkModalPortal>

            {onMove ? (
                <MoveTasksModal
                    open={showMove}
                    itemCount={selectedCount}
                    itemNames={itemNames}
                    destinations={destinations}
                    onClose={() => setShowMove(false)}
                    onMove={handleMove}
                    loading={moveLoading}
                />
            ) : null}
        </>
    );
};
